import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

import Modal from 'components/Modal'

import { shortTimeDate } from 'helpers/formatters'

import { color } from 'styles/colors'
import { ButtonContainer, screenSize } from 'styles/shared'

import useCognitoAuthToken from 'hooks/useCognitoAuthToken'

import { Status } from 'types/RemoteConfig'
import { DeviceBase } from 'types/Device'

import Api from 'services/api'

type Props = {
  status: Status
  device: DeviceBase
}

const RequestRemoteStatus = ({ status, device }: Props) => {
  const [showDRCContanier, setShowDRCContainer] = useState(false)
  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [interpretedState, setInterpretedState] = useState(status.state)

  const { authToken, isAuthTokenLoaded } = useCognitoAuthToken()

  const onStopRemoteConfig = async () => {
    setButtonDisabled(true)

    await Api.RemoteConfig.stop(device.deviceId)
  }

  useEffect(() => {
    setInterpretedState(status.state)
  }, [status.state])

  useEffect(() => {
    if (!status.expiresAt || !status.acceptedAt) return

    const secondsUntilExpire = (new Date(status.expiresAt).getTime() - new Date(status.acceptedAt).getTime())
    const timer = setTimeout(() => {
      setInterpretedState('expired')
    }, secondsUntilExpire)

    return () => clearTimeout(timer)
  }, [status.expiresAt, status.acceptedAt])

  const requestState = (state: Status['state']) => {
    switch (state) {
      case 'pending':
        return 'Remote Access Pending'
      case 'active':
        return 'Remote Session Active'
      case 'accepted':
        return 'Remote Access Granted'
      case 'denied':
        return 'Remote Access Denied'
      case 'expired':
        return 'Remote Session Expired'
      case 'stopped':
        return 'Remote Session Stopped'
    }
  }

  const message = (status: Status) => {
    switch (interpretedState) {
      case 'pending':
        return 'Remote access requested ' + shortTimeDate(status.requestedAt)
      case 'active':
        return 'Expires at ' + shortTimeDate(status.expiresAt)
      case 'accepted':
        return 'Expires at ' + shortTimeDate(status.expiresAt)
      case 'denied':
        return 'Remote access denied at ' + shortTimeDate(status.deniedAt)
      case 'expired':
        return 'Expired at ' + shortTimeDate(status.expiresAt)
      case 'stopped':
        return 'Stopped at ' + shortTimeDate(status.stoppedAt)
    }
  }

  const rightButtonName = (state: Status['state']) => {
    switch (state) {
      case 'pending':
        return 'Cancel'
      case 'accepted':
      case 'active':
        return 'Done'
      case 'denied':
      case 'expired':
      case 'stopped':
        return 'Close'
    }
  }

  const isValidState = ['accepted', 'active', 'pending', 'denied', 'expired', 'stopped'].includes(interpretedState ?? '')

  const isControlHub = device.deviceType === 'control_hub'
  const isCopeland = device.deviceModel === '24P' || device.deviceModel === '24C'

  if (!isAuthTokenLoaded) {
    return null
  }

  return (
    <S.RequestRemoteStatus>
      {
        isValidState && (
          <S.Status>
            <S.Header>{requestState(interpretedState)}</S.Header>
            <S.Message>{message(status)}</S.Message>
            <S.ButtonContainer>
              {!isControlHub && !isCopeland && ['accepted', 'active'].includes(interpretedState) && <Button styles={ButtonStyle} decoration="positive" text="Start" onPress={() => setShowDRCContainer(true)} />}
              {!isControlHub && !isCopeland && <Button styles={ButtonStyle} decoration="secondary" enabled={!buttonDisabled} text={rightButtonName(interpretedState) || ''} onPress={onStopRemoteConfig} />}
              {(isControlHub || isCopeland) && <S.LinkDeviceInfo>Use the Technician App to access a Remote Session for this device.</S.LinkDeviceInfo>}
            </S.ButtonContainer>
          </S.Status>
        )
      }
      {showDRCContanier && (
        <Modal isDisplayed closeCallback={() => setShowDRCContainer(false)}>
          <div>
            <S.Iframe src={Api.RemoteConfig.iframeUrl(device.deviceId, authToken || '')} title="DRC" aria-label="drc session" />
            <ButtonContainer noBorder>
              <Button styles={ButtonStyle} decoration="positive" text="Close" onPress={() => setShowDRCContainer(false)} />
            </ButtonContainer>
          </div>
        </Modal>
      )}
    </S.RequestRemoteStatus>
  )
}

export default RequestRemoteStatus

const ButtonStyle: ButtonStyles = { button: { borderRadius: 14 } }

const S = {
  RequestRemoteStatus: styled.div`
    margin-right: 15px;
  `,
  Status: styled.div`
    border: 1px solid ${color('border')};
    border-radius: 16px;
    padding: 12px 10px 12px 10px;
    margin-bottom: 10px;
    width: 200px;
    font-weight: 700;
    font-size: 1rem;

    @media ${screenSize.small} {
      width: 100%;
    }
  `,
  Header: styled.div`
    text-transform: capitalize;
  `,
  Message: styled.div`
    padding-top: 4px;
    color: ${color('ancillaryText')}
  `,
  ButtonContainer: styled.div`
    margin-top: 28px;
    display: flex;
    flex-direction: row-reverse;
    gap: 16px;
  `,
  LinkDeviceInfo: styled.div`
    @media ${screenSize.small} {
      margin-bottom: 15px;
    }
  `,
  Iframe: styled.iframe`
    min-width: 804px;
    min-height: 515px;
    width: 100%;
    height: 100%;
    border: none;
  `
}
